import { PLATFORM_LABELS } from "../platforms";
import { ApiError } from "../api";
import { ownValue, safeMessage, tidy } from "./shared/safe";

// Pure logic for the Queue view: parsing untrusted job rows, deciding what may be offered, and wording
// failures. No DOM, no network, so it can be tested exhaustively.

export const STATUS_FILTERS = ["all", "queued", "running", "failed", "dead", "done"] as const;
export type StatusFilter = (typeof STATUS_FILTERS)[number];

/** The jobs endpoint returns at most this many rows (apps/api routes/jobs.ts: LIMIT 200). */
export const JOBS_LIMIT = 200;

/** Prefixes the API writes into last_error (services/jobs/types.ts). Legacy signals only: newer rows also carry side_effect. */
export const OUTCOME_UNKNOWN_PREFIX = "OUTCOME_UNKNOWN: ";
export const EFFECT_CONFIRMED_PREFIX = "EFFECT_CONFIRMED: ";

const JOB_TYPE_LABELS: Record<string, string> = { daily_cycle: "Daily content cycle", deploy: "Deploy", verify: "Verify" };
const KNOWN_STATUSES = ["queued", "running", "failed", "dead", "done"] as const;

/** Validates a filter value against the real list (never `in`), falling back to All. */
export function parseFilter(raw: unknown): StatusFilter {
  return typeof raw === "string" && (STATUS_FILTERS as readonly string[]).includes(raw) ? (raw as StatusFilter) : "all";
}

/**
 * What A's GET /v1/jobs sends in `jobs[].actions.retry` (apps/api/src/services/jobs/eligibility.ts):
 *   { eligible: true, basis }                                                   or
 *   { eligible: false, code, reason, message, requiresReconciliation }
 * `message` is the human explanation, `reason` and `code` are machine values. This type is the parsed, validated form.
 */
export type RetryAction =
  | { state: "absent" } // no capability metadata at all (an older server): the legacy rule applies
  | { state: "malformed" } // capability metadata is present but unreadable: fail closed
  | { state: "eligible"; basis: string | null }
  | { state: "denied"; code: string | null; reason: string | null; message: string | null; requiresReconciliation: boolean | null };

export interface Job {
  /** Null when the row carried no usable id: it is shown, but nothing can be done to it. */
  id: string | null;
  type: string;
  clientId: string | null;
  status: string;
  attempts: number | null;
  lastError: string | null;
  platform: string | null;
  createdAt: unknown;
  /** From `actions.retry`. */
  retryAction: RetryAction;
  /** Raw recorded columns (migration 0005). Used only to fail closed when they contradict the server's metadata. */
  sideEffect: string | null;
  stateVersion: number | null;
  /** "pending": the job is durably accepted and waiting for the dispatcher to put it on the queue. Not a failure, not delivery. */
  dispatch: string | null;
  /** A denial in the obsolete top-level `retry` field (round-3 guess). It can deny, never grant. */
  legacyDenial: { reason: string | null } | null;
}

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);

/** Platform named by a deploy job's payload; null for a missing, malformed or unexpected payload. Nothing else is read. */
export function payloadPlatform(payloadJson: unknown): string | null {
  if (typeof payloadJson !== "string" || payloadJson.trim() === "") return null;
  try {
    const parsed: unknown = JSON.parse(payloadJson);
    return isObject(parsed) && typeof parsed.platform === "string" && parsed.platform !== "" ? parsed.platform : null;
  } catch {
    return null;
  }
}

const text = (v: unknown): string | null => (typeof v === "string" && v !== "" ? v : null);

/**
 * Reads `actions.retry`. ONLY a row with no `actions` property at all (an older server) is "absent" and may use the
 * legacy fallback. A present `actions` that is null, not an object, or lacks a readable `retry` fails closed, and so
 * does an "eligible" answer with no basis: a server that grants Retry always says why.
 */
export function readRetryAction(raw: Record<string, unknown>): RetryAction {
  if (!("actions" in raw)) return { state: "absent" };
  if (!isObject(raw.actions)) return { state: "malformed" };
  const retry = raw.actions.retry;
  if (!isObject(retry) || typeof retry.eligible !== "boolean") return { state: "malformed" };
  if (retry.eligible) {
    const basis = text(retry.basis);
    return basis === null ? { state: "malformed" } : { state: "eligible", basis };
  }
  return {
    state: "denied",
    code: text(retry.code),
    reason: text(retry.reason),
    message: text(retry.message),
    requiresReconciliation: typeof retry.requiresReconciliation === "boolean" ? retry.requiresReconciliation : null,
  };
}

function readLegacyDenial(raw: unknown): { reason: string | null } | null {
  return isObject(raw) && raw.eligible === false ? { reason: text(raw.reason) } : null;
}

export function normalizeJob(raw: unknown): Job | null {
  if (!isObject(raw)) return null;
  const attempts = typeof raw.attempts === "number" && Number.isFinite(raw.attempts) ? raw.attempts : null;
  return {
    id: typeof raw.id === "string" && raw.id !== "" ? raw.id : null,
    type: typeof raw.type === "string" ? raw.type : "",
    clientId: typeof raw.client_id === "string" && raw.client_id !== "" ? raw.client_id : null,
    status: typeof raw.status === "string" && raw.status !== "" ? raw.status : "unknown",
    attempts,
    lastError: typeof raw.last_error === "string" && raw.last_error !== "" ? raw.last_error : null,
    platform: payloadPlatform(raw.payload_json),
    createdAt: raw.created_at,
    retryAction: readRetryAction(raw),
    sideEffect: text(raw.side_effect),
    stateVersion: typeof raw.state_version === "number" && Number.isFinite(raw.state_version) ? raw.state_version : null,
    dispatch: text(raw.dispatch),
    legacyDenial: readLegacyDenial(raw.retry),
  };
}

export interface JobsPage {
  limit: number;
  returned: number | null;
  /** Authoritative when the server sent it; otherwise inferred from the legacy 200-row cap. */
  mayBeTruncated: boolean | null;
}

/** `page` from A's list response ({ limit, returned, mayBeTruncated }); null when absent or unreadable. */
export function readPage(body: Record<string, unknown>): JobsPage | null {
  const p = body.page;
  if (!isObject(p) || typeof p.limit !== "number" || !Number.isFinite(p.limit) || p.limit <= 0) return null;
  return {
    limit: Math.floor(p.limit),
    returned: typeof p.returned === "number" && Number.isFinite(p.returned) ? p.returned : null,
    mayBeTruncated: typeof p.mayBeTruncated === "boolean" ? p.mayBeTruncated : null,
  };
}

/** Parses the response body; rows that are not objects are dropped and counted, never rendered. */
export function parseJobs(body: unknown): { jobs: Job[]; dropped: number; page: JobsPage | null } | null {
  if (!isObject(body) || !Array.isArray(body.jobs)) return null;
  const jobs: Job[] = [];
  let dropped = 0;
  for (const raw of body.jobs) {
    const job = normalizeJob(raw);
    if (job) jobs.push(job);
    else dropped += 1;
  }
  return { jobs, dropped, page: readPage(body) };
}

/**
 * Human label for a platform key taken from data. platformLabel() in platforms.ts reads a plain object, so a key
 * such as "toString" would return a function; this looks up own keys only and title-cases anything unknown.
 */
export function safePlatformLabel(platform: string): string {
  return ownValue(PLATFORM_LABELS, platform) ?? tidy(platform.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" "), 60);
}

export function jobLabel(job: Job): string {
  if (job.type === "deploy" && job.platform) return `Deploy to ${safePlatformLabel(job.platform)}`;
  return ownValue(JOB_TYPE_LABELS, job.type) ?? (job.type ? tidy(job.type, 40) : "Unknown job type");
}

export function statusTone(status: string): "ok" | "warn" | "bad" | "neutral" {
  return status === "done" ? "ok" : status === "failed" || status === "dead" ? "bad" : "neutral";
}

export const isKnownStatus = (status: string): boolean => (KNOWN_STATUSES as readonly string[]).includes(status);

export const hasUnknownOutcome = (job: Job): boolean =>
  job.status === "dead" && (job.sideEffect === "possible" || (job.lastError !== null && job.lastError.startsWith(OUTCOME_UNKNOWN_PREFIX)));
export const hasConfirmedEffect = (job: Job): boolean =>
  job.status === "dead" && (job.sideEffect === "confirmed" || (job.lastError !== null && job.lastError.startsWith(EFFECT_CONFIRMED_PREFIX)));

export const UNKNOWN_OUTCOME_TEXT =
  "Outcome unknown. The provider call ended without a receipt, so the post may already exist. Retry is blocked until the outcome is reconciled with the provider. Use Reconcile evidence to inspect attributable receipts; an unrecorded ambiguous write stays blocked.";
export const EFFECT_CONFIRMED_TEXT =
  "The provider reported success but the result could not be fully recorded. Retrying could duplicate it, so Retry is blocked. Verify the destination; use Reconcile evidence to inspect attributable receipts; an unrecorded ambiguous write stays blocked.";
export const AUTOMATIC_RETRY_TEXT = "The queue is retrying this job automatically. A manual retry is neither needed nor available.";
export const MALFORMED_RETRY_TEXT = "This row's retry information could not be read, so Retry is not offered. Refresh; if it persists, the server's reply is malformed.";

export type RetryDecision =
  | { kind: "offer" }
  | { kind: "blocked_unknown"; reason: string; code: string | null; machineReason: string | null }
  | { kind: "blocked_confirmed"; reason: string; code: string | null; machineReason: string | null }
  | { kind: "automatic"; reason: string; code: string | null; machineReason: string | null }
  | { kind: "server_denied"; reason: string; code: string | null; machineReason: string | null; requiresReconciliation: boolean | null }
  | { kind: "malformed"; reason: string }
  | { kind: "no_id"; reason: string }
  | { kind: "none" };

/**
 * What the Queue may offer for one row.
 *
 *  - The server's `actions.retry` is authoritative. A denial is final: its `message` is shown, its `code` and `reason`
 *    are kept (data-retry-code / data-retry-reason) and EFFECT_CONFIRMED is told apart from OUTCOME_UNKNOWN.
 *  - Capability metadata that is present but unreadable fails closed (no Retry). Only ABSENT metadata (an older
 *    server) falls back to the conservative legacy rule: dead, and no side_effect possible/confirmed, and no text marker.
 *  - Conflicts fail closed too: if recorded side-effect data contradicts an "eligible" answer, or an obsolete top-level
 *    `retry` field denies, the row is blocked. Nothing can grant what a denial or a recorded effect refuses.
 *  - A job the API refused because its pinned account changed (ACCOUNT_CHANGED / ACCOUNT_PROVENANCE_UNPINNED) is now denied by the
 *    server itself (`eligible: false`, `retryable: false`, "Publish again"); the page just shows that denial. It used to be offered Retry
 *    by the server, and the page had a client-side guard for it; the guard was removed once the server contract covered both codes.
 *  - `failed` is never manually retryable: the queue is redelivering it.
 *  - There is no acknowledgement shortcut and no reconcile action: an acknowledgement is not evidence that nothing was posted.
 *  - A button is not authority: the server can still refuse, and that refusal is shown.
 */
export function retryDecision(job: Job): RetryDecision {
  const action = job.retryAction;
  const confirmed = hasConfirmedEffect(job);
  const unknown = hasUnknownOutcome(job);

  if (action.state === "denied") {
    const code = action.code;
    const machine = action.reason;
    if (code === "EFFECT_CONFIRMED" || machine === "effect_confirmed") return { kind: "blocked_confirmed", reason: action.message ? safeMessage(action.message, 400) : EFFECT_CONFIRMED_TEXT, code, machineReason: machine };
    if (code === "OUTCOME_UNKNOWN" || machine === "outcome_unknown") return { kind: "blocked_unknown", reason: action.message ? safeMessage(action.message, 400) : UNKNOWN_OUTCOME_TEXT, code, machineReason: machine };
    if (job.status === "failed") return { kind: "automatic", reason: action.message ? safeMessage(action.message, 400) : AUTOMATIC_RETRY_TEXT, code, machineReason: machine };
    if (job.status !== "dead" && code === "JOB_NOT_RETRYABLE") return { kind: "none" };
    return {
      kind: "server_denied",
      reason: action.message ? safeMessage(action.message, 400) : "The server says this job cannot be retried.",
      code,
      machineReason: machine,
      requiresReconciliation: action.requiresReconciliation,
    };
  }
  if (action.state === "malformed") return { kind: "malformed", reason: MALFORMED_RETRY_TEXT };

  // From here the server either said "eligible" or sent nothing. Recorded effects and obsolete denials still win.
  if (job.status === "failed") return { kind: "automatic", reason: AUTOMATIC_RETRY_TEXT, code: null, machineReason: null };
  if (job.status !== "dead") return { kind: "none" };
  if (confirmed) return { kind: "blocked_confirmed", reason: EFFECT_CONFIRMED_TEXT, code: "EFFECT_CONFIRMED", machineReason: "effect_confirmed" };
  if (unknown) return { kind: "blocked_unknown", reason: UNKNOWN_OUTCOME_TEXT, code: "OUTCOME_UNKNOWN", machineReason: "outcome_unknown" };
  if (job.legacyDenial) {
    return { kind: "server_denied", reason: job.legacyDenial.reason ? `Retry is not available: ${safeMessage(job.legacyDenial.reason, 200)}` : "The server says this job cannot be retried.", code: null, machineReason: null, requiresReconciliation: null };
  }
  if (job.id === null) return { kind: "no_id", reason: "This row has no job id, so it can't be retried from here." };
  return { kind: "offer" };
}

export interface Notice {
  tone: "success" | "warning" | "error";
  message: string;
}

export interface RetryAck {
  jobId: string;
  /** "sent": on the queue. "pending": durably accepted, waiting for the dispatcher. null: an older server that does not say. */
  dispatch: "sent" | "pending" | null;
  /** The server recognised the Idempotency-Key and returned the ORIGINAL result. */
  replayed: boolean;
}

/**
 * A positive acknowledgement of a retry: `requeued === true` AND the id of the job that was asked about. Anything else
 * (empty body, wrong shape, an acknowledgement for a different job) is not one.
 */
export function readRetryAck(body: unknown, expectedJobId?: string): RetryAck | null {
  if (!isObject(body) || body.requeued !== true || typeof body.jobId !== "string" || body.jobId === "") return null;
  if (expectedJobId !== undefined && body.jobId !== expectedJobId) return null;
  return { jobId: body.jobId, dispatch: body.dispatch === "sent" || body.dispatch === "pending" ? body.dispatch : null, replayed: body.replayed === true };
}

export { isDefinitiveRefusal } from "./shared/intent";

/** Wording for a failed retry request. Never claims success; says plainly what is and isn't known. */
export function describeRetryError(error: unknown): Notice & { refresh: boolean } {
  if (error instanceof ApiError) {
    const server = safeMessage(error.message, 200);
    const base = (message: string, refresh = true): Notice & { refresh: boolean } => ({ tone: "error", message, refresh });
    switch (error.status) {
      case 401:
        return base("The dashboard token was rejected, so nothing was re-queued. Sign in again.", false);
      case 403:
        return base("You don't have permission to retry jobs. Nothing was re-queued.", false);
      case 404:
        return base("This job no longer exists. Nothing was re-queued.");
      case 409:
        // The server's own refusal text is the explanation; it is ~250 characters, so it is not cut at the usual 200.
        if (error.code === "EFFECT_CONFIRMED") return base(error.message ? `${safeMessage(error.message, 500)} (Retry is blocked: the effect is confirmed; nothing was re-queued.)` : EFFECT_CONFIRMED_TEXT);
        if (error.code === "ACCOUNT_CHANGED" || error.code === "ACCOUNT_PROVENANCE_UNPINNED") return base(`${safeMessage(error.message, 500)} (Retry can never succeed for this job; nothing was re-queued.)`);
        if (error.code === "OUTCOME_UNKNOWN") return base(error.message ? `${safeMessage(error.message, 500)} (Retry is blocked: the outcome is unknown; nothing was re-queued.)` : UNKNOWN_OUTCOME_TEXT);
        if (error.code === "IDEMPOTENCY_IN_PROGRESS") return base(`Another request with the same key is still being processed, so nothing new was queued. Try again in a moment: repeating it is safe and returns the original result. Server: ${server}`, false);
        if (error.code === "IDEMPOTENCY_CONFLICT") return base(`The server says this request key was already used for a different request, so nothing was re-queued. Server: ${server}`);
        return base(`The job's state changed, so it was not re-queued. Server: ${server}`);
      case 422:
        return base(`This job can't be retried: ${server}`);
      case 500:
        return base(`The server could not finish the retry and the job's state may be uncertain. Nothing is confirmed as re-queued. Server: ${server} Refresh and check the job; Retry repeats the same request safely.`);
      case 502:
        return base(`The job could not be put on the queue. It was not re-queued. Server: ${server}`);
      default:
        return base(`The server refused the request (HTTP ${error.status}). Nothing is confirmed as re-queued. Server: ${server}`);
    }
  }
  return { tone: "error", refresh: true, message: "Couldn't reach the server, so it is not known whether the job was re-queued. The list below was refreshed from the server. If the job is still dead, Retry repeats the same request with the same key, so it cannot queue the job twice." };
}
